/**
 * The code emblem kit (M6 plan P33, spec §7.1; placeholders for the GPT-image drafts of D-028). A logo is a
 * `LogoRecipe`: a seed, the draft picked (one of corp.json `logo.drafts` seeded drafts), its shape and palette, and the
 * player's finishing steps. The finishing steps are the player's own work and are recorded as `humanContribution`
 * (D-026). Recipes serialise as JSON validated with Zod (`parseLogo`). Pure; UI rate only.
 */
import { z } from 'zod'
import corpJson from '../../data/strategic/corp.json'
import type { LogoRecipe } from '../../strategic/types'
import screenJson from './corpScreen.json'

/** The kit's sizes and the editor's step (corp.json `logo`, corpScreen.json `kit`). */
export interface LogoKit {
  drafts: number
  shapes: readonly string[]
  palettes: readonly (readonly [string, string, string])[]
  rotateStep: number
}

const kitShape = z.object({
  shapes: z.array(z.string()).min(1),
  palettes: z.array(z.tuple([z.string(), z.string(), z.string()])).min(1),
  rotateStep: z.number().int().positive(),
})

let cached: LogoKit | null = null

/** The bundled kit (parsed once). */
export function logoKit(): LogoKit {
  if (cached !== null) return cached
  const k = kitShape.parse(screenJson.kit)
  const counts = corpJson.logo
  if (k.shapes.length !== counts.shapes || k.palettes.length !== counts.palettes) {
    throw new Error(
      `logo kit: corpScreen.json has ${k.shapes.length} shapes / ${k.palettes.length} palettes, corp.json says ${counts.shapes} / ${counts.palettes}`,
    )
  }
  cached = {
    drafts: counts.drafts,
    shapes: k.shapes,
    palettes: k.palettes,
    rotateStep: k.rotateStep,
  }
  return cached
}

/** One finishing step: `shape:<i>`, `palette:<i>`, `rotate:<deg>`, `border:<0|1>` or `invert:<0|1>`. */
export type LogoOp = 'shape' | 'palette' | 'rotate' | 'border' | 'invert'
export const LOGO_OPS: readonly LogoOp[] = ['shape', 'palette', 'rotate', 'border', 'invert']
const STEP_RE = /^(shape|palette|rotate|border|invert):(-?\d{1,4})$/
export const MAX_FINISHING = 64

export const logoRecipeSchema = z.strictObject({
  seed: z.number().int().min(0).max(0xffffffff),
  draft: z.number().int().min(0),
  shape: z.number().int().min(0),
  palette: z.number().int().min(0),
  finishing: z.array(z.string().regex(STEP_RE)).max(MAX_FINISHING),
})

/** A 32-bit hash of (seed, a, b) (mulberry-style mix). */
function mix(seed: number, a: number, b: number): number {
  let h = (seed ^ Math.imul(a + 1, 0x9e3779b1) ^ Math.imul(b + 7, 0x85ebca6b)) >>> 0
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d) >>> 0
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b) >>> 0
  return (h ^ (h >>> 16)) >>> 0
}

/** The kit's seeded drafts for `seed`: distinct shapes where the kit allows, no finishing yet. */
export function seededDrafts(seed: number, kit: LogoKit = logoKit()): LogoRecipe[] {
  const out: LogoRecipe[] = []
  const used = new Set<number>()
  for (let i = 0; i < kit.drafts; i++) {
    let shape = mix(seed, i, 0) % kit.shapes.length
    for (let k = 0; used.has(shape) && k < kit.shapes.length; k++) shape = (shape + 1) % kit.shapes.length
    used.add(shape)
    out.push({
      seed: seed >>> 0,
      draft: i,
      shape,
      palette: mix(seed, i, 1) % kit.palettes.length,
      finishing: [],
    })
  }
  return out
}

/** What a recipe draws: the shape and palette after finishing, the rotation, a border ring, inverted colours. */
export interface ResolvedLogo {
  shape: number
  palette: number
  rotation: number
  border: boolean
  invert: boolean
}

function wrap(i: number, n: number): number {
  return ((i % n) + n) % n
}

/** Replays a recipe's finishing over its draft (from the seed, so a recipe's own shape/palette fields are a cache). */
export function resolveLogo(recipe: LogoRecipe, kit: LogoKit = logoKit()): ResolvedLogo {
  const base = seededDrafts(recipe.seed, kit)[recipe.draft] ?? {
    shape: recipe.shape,
    palette: recipe.palette,
  }
  const r: ResolvedLogo = {
    shape: base.shape,
    palette: base.palette,
    rotation: 0,
    border: false,
    invert: false,
  }
  for (const step of recipe.finishing) {
    const m = STEP_RE.exec(step)
    if (m === null) continue
    const v = Number(m[2])
    switch (m[1] as LogoOp) {
      case 'shape':
        r.shape = wrap(v, kit.shapes.length)
        break
      case 'palette':
        r.palette = wrap(v, kit.palettes.length)
        break
      case 'rotate':
        r.rotation = wrap(v, 360)
        break
      case 'border':
        r.border = v !== 0
        break
      case 'invert':
        r.invert = v !== 0
        break
    }
  }
  return r
}

/** The recipe with one more finishing step (immutable): `op` set to `value`; shape/palette caches follow. */
export function applyStep(recipe: LogoRecipe, op: LogoOp, value: number, kit: LogoKit = logoKit()): LogoRecipe {
  if (recipe.finishing.length >= MAX_FINISHING) return recipe
  const v = Math.trunc(value)
  const finishing = [...recipe.finishing, `${op}:${v}`]
  const r = resolveLogo({ ...recipe, finishing }, kit)
  return { ...recipe, finishing, shape: r.shape, palette: r.palette }
}

/** The next value of `op` from the resolved logo (cycling shapes, palettes, rotation by the step, toggles). */
export function nextValue(logo: ResolvedLogo, op: LogoOp, dir: 1 | -1, kit: LogoKit = logoKit()): number {
  switch (op) {
    case 'shape':
      return wrap(logo.shape + dir, kit.shapes.length)
    case 'palette':
      return wrap(logo.palette + dir, kit.palettes.length)
    case 'rotate':
      return wrap(logo.rotation + dir * kit.rotateStep, 360)
    case 'border':
      return logo.border ? 0 : 1
    case 'invert':
      return logo.invert ? 0 : 1
  }
}

/** Starts again from draft `draft` of the same seed (the finishing is dropped). */
export function pickDraft(seed: number, draft: number, kit: LogoKit = logoKit()): LogoRecipe {
  const d = seededDrafts(seed, kit)
  return d[wrap(draft, d.length)]
}

/** The recipe as JSON text (the save and the provenance record). */
export function serializeLogo(recipe: LogoRecipe): string {
  return JSON.stringify(logoRecipeSchema.parse(recipe))
}

/** A recipe from JSON text, or null when it is not a valid recipe for `kit` (Zod plus the kit's ranges). */
export function parseLogo(text: string, kit: LogoKit = logoKit()): LogoRecipe | null {
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch {
    return null
  }
  const r = logoRecipeSchema.safeParse(raw)
  if (!r.success) return null
  const v = r.data
  if (v.draft >= kit.drafts || v.shape >= kit.shapes.length || v.palette >= kit.palettes.length) return null
  return v
}

/**
 * The `humanContribution` line for the provenance record (D-026): which seeded draft was picked and the player's
 * finishing steps, in order. A logo with no finishing is a plain pick.
 */
export function humanContribution(recipe: LogoRecipe, kit: LogoKit = logoKit()): string {
  const pick = `Picked code-kit draft ${recipe.draft + 1} of ${kit.drafts} (seed ${recipe.seed})`
  if (recipe.finishing.length === 0) return `${pick}; no finishing.`
  return `${pick}; finished by the player in ${recipe.finishing.length} step${recipe.finishing.length === 1 ? '' : 's'}: ${recipe.finishing.join(', ')}.`
}

// ---------------------------------------------------------------------------------------------
// The emblem shapes (100 × 100 box, centred on 50,50), in corpScreen.json `kit.shapes` order
// ---------------------------------------------------------------------------------------------

function polygon(n: number, r: number, start = -90): string {
  const pts: string[] = []
  for (let i = 0; i < n; i++) {
    const a = ((start + (360 * i) / n) * Math.PI) / 180
    pts.push(`${(50 + r * Math.cos(a)).toFixed(2)},${(50 + r * Math.sin(a)).toFixed(2)}`)
  }
  return `M${pts.join(' L')} Z`
}

function star(n: number, outer: number, inner: number): string {
  const pts: string[] = []
  for (let i = 0; i < n * 2; i++) {
    const r = i % 2 === 0 ? outer : inner
    const a = ((-90 + (180 * i) / n) * Math.PI) / 180
    pts.push(`${(50 + r * Math.cos(a)).toFixed(2)},${(50 + r * Math.sin(a)).toFixed(2)}`)
  }
  return `M${pts.join(' L')} Z`
}

const SHAPES: readonly string[] = [
  polygon(3, 34),
  polygon(4, 32, -45),
  polygon(5, 32),
  polygon(6, 32),
  polygon(7, 32),
  polygon(8, 32),
  star(5, 34, 14),
  star(6, 34, 18),
  'M20,30 L50,52 L80,30 L80,48 L50,72 L20,48 Z',
  'M40,18 L60,18 L60,40 L82,40 L82,60 L60,60 L60,82 L40,82 L40,60 L18,60 L18,40 L40,40 Z',
  'M50,16 A34,34 0 0 1 84,50 L68,50 A18,18 0 0 0 50,32 Z M50,84 A34,34 0 0 1 16,50 L32,50 A18,18 0 0 0 50,68 Z',
  'M50,16 L80,26 L78,54 Q72,74 50,86 Q28,74 22,54 L20,26 Z',
]

/** The SVG path of kit shape `i`. */
export function shapePath(i: number): string {
  return SHAPES[wrap(i, SHAPES.length)]
}

/** The colours a resolved logo draws with: field, emblem, accent (swapped when inverted). */
export function logoColors(logo: ResolvedLogo, kit: LogoKit = logoKit()): { field: string; emblem: string; accent: string } {
  const [a, b, c] = kit.palettes[wrap(logo.palette, kit.palettes.length)]
  return logo.invert ? { field: b, emblem: a, accent: c } : { field: a, emblem: b, accent: c }
}
